import { PropsWithChildren, useCallback, useState } from 'react'

import { NotificationSeverity } from '@/Shared/domain'
import { createGenericContext } from '@/Shared/ui/createGenericContext'

export type NotificationContextProps = PropsWithChildren & {
  notification: INotification
  addNotification: (message: string, severity: NotificationSeverity) => void
  removeNotification: () => void
}

export interface INotification {
  message: string
  severity: NotificationSeverity
}

const [useNotification, StateContextProvider] =
  createGenericContext<NotificationContextProps>()

const NotificationProvider = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => {
  const [notification, setNotification] = useState<INotification>({
    message: '',
    severity: NotificationSeverity.success,
  })

  const addNotification = (message: string, severity: NotificationSeverity): void =>
    setNotification({ message, severity })

  const removeNotification = (): void =>
    setNotification({
      ...notification,
      message: '',
      severity: NotificationSeverity.success,
    })

  const value = {
    notification,
    addNotification: useCallback(
      (message: string, severity: NotificationSeverity) =>
        addNotification(message, severity),
      [],
    ),
    removeNotification: useCallback(() => removeNotification(), []),
  }

  return <StateContextProvider value={value}>{children}</StateContextProvider>
}

export { NotificationProvider, useNotification }
